import * as React from 'react';
import { StyleSheet, View, Text, Image, TouchableOpacity } from 'react-native';
import { font } from '../../style/text';
import color from '../../style/color';
import { getLocalizedTexts } from '../../Locales';
import type { OrderData } from './giftCardPaymentModel';

export { OrderData };

const applePayLogo = { uri: '/assets/images/logos/apple-pay.svg' };
const googlePayLogo = { uri: '/assets/images/logos/google-pay.svg' };
const creditCardLogo = { uri: '/assets/images/icons/credit-card-simple.svg' };
const infoIcon = { uri: '/assets/images/icons/info.svg' };

export const PAYMENT_METHOD_LOGOS = { applePayLogo, googlePayLogo, creditCardLogo };

export function WalletMethodRow({
    logo,
    label,
    selected,
    onPress,
    hasBorderBottom = false,
}: {
    logo: { uri: string };
    label: string;
    selected: boolean;
    onPress: () => void;
    hasBorderBottom?: boolean;
}) {
    return (
        <TouchableOpacity
            style={[styles.walletMethodRow, hasBorderBottom && styles.walletMethodRowBorder]}
            onPress={onPress}
            activeOpacity={0.7}
        >
            <View style={styles.walletMethodLogoContainer}>
                <Image source={logo} style={styles.walletMethodLogo} />
            </View>
            <Text style={styles.walletMethodLabel}>{label}</Text>
            <View style={[styles.walletMethodRadio, selected && styles.walletMethodRadioSelected]}>
                {selected && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 3.5L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </View>
        </TouchableOpacity>
    );
}

export function ConditionsBlock({
    conditions,
    secondaryConditions,
    conditionsOfUseUrl,
}: {
    conditions: Array<{ text: string; shouldBeCrossedOut?: boolean }>;
    secondaryConditions?: Array<{ text: string; shouldBeCrossedOut?: boolean }>;
    conditionsOfUseUrl?: string;
}) {
    const texts = getLocalizedTexts().giftCardPaymentPage;
    const [isExpanded, setIsExpanded] = React.useState(false);
    const hasSecondary = secondaryConditions && secondaryConditions.length > 0;
    const allConditions = isExpanded && hasSecondary
        ? [...conditions, ...secondaryConditions]
        : conditions;
    return (
        <View style={styles.conditionsBlock}>
            <Text style={styles.conditionsBlockTitle}>{texts.importantConditions}</Text>
            {allConditions.map((condition, index) => (
                <View
                    key={index}
                    style={[
                        styles.conditionRow,
                        index < allConditions.length - 1 && styles.conditionRowBorder,
                    ]}
                >
                    <Image source={infoIcon} style={styles.conditionIcon} />
                    <Text
                        style={[
                            styles.conditionText,
                            condition.shouldBeCrossedOut && styles.conditionTextCrossedOut,
                        ]}
                    >
                        {condition.text}
                    </Text>
                </View>
            ))}
            {hasSecondary && !isExpanded && (
                <TouchableOpacity
                    style={styles.seeAllConditionsButton}
                    onPress={() => setIsExpanded(true)}
                    activeOpacity={0.7}
                >
                    <Text style={styles.seeAllConditionsText}>{texts.seeMore}</Text>
                </TouchableOpacity>
            )}
            {conditionsOfUseUrl && (
                <TouchableOpacity
                    style={styles.seeAllConditionsButton}
                    onPress={() => window.open(conditionsOfUseUrl, '_blank')}
                    activeOpacity={0.7}
                >
                    <Text style={styles.seeAllConditionsText}>{texts.seeAllConditions}</Text>
                </TouchableOpacity>
            )}
        </View>
    );
}

export function GiftCardPreview({ orderData, numberOfCards }: { orderData: OrderData; numberOfCards: number }) {
    const texts = getLocalizedTexts().giftCardPaymentPage;
    const bgColor = orderData.cardBackgroundColor ?? color.black;
    const textColor = orderData.cardTextColor ?? color.white;
    return (
        <View style={[styles.giftCard, { backgroundColor: bgColor }]}>
            <View style={styles.giftCardTop}>
                {orderData.brandLogoUrl ? (
                    <Image source={{ uri: orderData.brandLogoUrl }} style={styles.giftCardBrandLogo} />
                ) : (
                    <Text style={[styles.giftCardBrandName, { color: textColor }]}>
                        {orderData.brandName}
                    </Text>
                )}
                {numberOfCards > 1 && (
                    <View style={styles.giftCardBadge}>
                        <Text style={styles.giftCardBadgeText}>{numberOfCards}</Text>
                    </View>
                )}
            </View>
            <View style={styles.giftCardCenter}>
                <Text style={[styles.giftCardAmount, { color: textColor }]}>
                    {orderData.totalAmountFormatted}
                </Text>
                {orderData.cashbackAmountFormatted && (
                    <View style={styles.cashbackBadge}>
                        <Text style={styles.cashbackBadgeAmount}>
                            {orderData.cashbackAmountFormatted}
                        </Text>
                        <Text style={styles.cashbackBadgeLabel}>{texts.immediateCashback}</Text>
                    </View>
                )}
            </View>
        </View>
    );
}

export function OrderSummary({ orderData }: { orderData: OrderData }) {
    const texts = getLocalizedTexts().giftCardPaymentPage;
    return (
        <View style={styles.orderCard}>
            <Text style={styles.orderTitle}>{texts.yourOrder}</Text>
            {orderData.items.map((item, index) => (
                <View key={index} style={styles.orderRow}>
                    <Text style={styles.orderItemLabel}>{item.label}</Text>
                    <Text style={styles.orderItemQty}>{item.quantity}x</Text>
                </View>
            ))}
            <View style={[styles.orderRow, styles.orderRowBorderTop]}>
                <Text style={styles.orderTotalLabel}>{texts.totalAmount}</Text>
                <Text style={styles.orderTotalValue}>{orderData.totalAmountFormatted}</Text>
            </View>

            {orderData.cashbackAmountFormatted && (
                <>
                    <Text style={[styles.orderTitle, styles.orderTitleBorderTop]}>{texts.yourCashback}</Text>
                    <Text style={styles.cashbackDescription}>
                        {orderData.cashbackDescription ?? texts.defaultCashbackDescription}
                    </Text>
                    <View style={styles.orderRow}>
                        <Text style={styles.orderItemLabel}>{texts.totalCashback}</Text>
                        <Text style={styles.orderTotalValue}>{orderData.cashbackAmountFormatted}</Text>
                    </View>
                </>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    walletMethodRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 14,
        backgroundColor: color.white,
        gap: 12,
    },
    walletMethodRowBorder: {
        borderBottomWidth: 1,
        borderBottomColor: '#E3E3E3',
    },
    walletMethodLogoContainer: {
        width: 48,
        height: 32,
        alignItems: 'center',
        justifyContent: 'center',
    },
    walletMethodLogo: {
        width: 48,
        height: 24,
        resizeMode: 'contain',
    },
    walletMethodLabel: {
        flex: 1,
        fontFamily: font.ambitSemiBold,
        fontSize: 16,
        lineHeight: 20,
        color: color.black,
    },
    walletMethodRadio: {
        width: 24,
        height: 24,
        borderRadius: 12,
        borderWidth: 1.5,
        borderColor: '#C4C4C4',
        alignItems: 'center',
        justifyContent: 'center',
    },
    walletMethodRadioSelected: {
        backgroundColor: color.black,
        borderColor: color.black,
    },

    conditionsBlock: {
        width: '100%',
        borderWidth: 1,
        borderColor: color.black,
        borderRadius: 16,
        marginBottom: 8,
        overflow: 'hidden',
    },
    conditionsBlockTitle: {
        fontFamily: font.ambitBold,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 8,
    },
    conditionRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingHorizontal: 16,
        paddingVertical: 12,
    },
    conditionRowBorder: {
        borderBottomWidth: 1,
        borderBottomColor: '#E3E3E3',
    },
    conditionIcon: {
        width: 16,
        height: 16,
        resizeMode: 'contain',
        flexShrink: 0,
    },
    conditionText: {
        fontFamily: font.ambitRegular,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
        flex: 1,
    },
    conditionTextCrossedOut: {
        textDecorationLine: 'line-through',
        opacity: 0.5,
    },
    seeAllConditionsButton: {
        margin: 16,
        marginTop: 8,
        borderWidth: 1,
        borderColor: color.black,
        borderRadius: 96,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    seeAllConditionsText: {
        fontFamily: font.ambitSemiBold,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
    },

    giftCard: {
        borderRadius: 16,
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 16,
        width: '100%',
        minHeight: 230,
        justifyContent: 'space-between',
    },
    giftCardTop: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    giftCardBrandLogo: {
        width: 48,
        height: 32,
        resizeMode: 'contain',
    },
    giftCardBrandName: {
        fontFamily: font.ambitBold,
        fontSize: 14,
        lineHeight: 17,
    },
    giftCardBadge: {
        backgroundColor: 'rgba(255,255,255,0.2)',
        borderRadius: 12,
        paddingHorizontal: 8,
        paddingVertical: 4,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    giftCardBadgeText: {
        fontFamily: font.ambitBold,
        fontSize: 14,
        lineHeight: 17,
        color: color.white,
    },
    giftCardCenter: {
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
    },
    giftCardAmount: {
        fontFamily: font.wallopSansBold,
        fontSize: 48,
        lineHeight: 58,
        letterSpacing: -0.96,
        textAlign: 'center',
    },
    cashbackBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginTop: 4,
    },
    cashbackBadgeAmount: {
        fontFamily: font.ambitBold,
        fontSize: 14,
        lineHeight: 17,
        color: color.white,
        backgroundColor: 'rgba(255,255,255,0.25)',
        borderRadius: 6,
        paddingHorizontal: 6,
        paddingVertical: 2,
        overflow: 'hidden',
    },
    cashbackBadgeLabel: {
        fontFamily: font.ambitRegular,
        fontSize: 16,
        lineHeight: 19,
        color: color.white,
    },

    orderCard: {
        backgroundColor: color.white,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#e3e3e3',
        padding: 16,
    },
    orderTitle: {
        fontFamily: font.ambitBold,
        fontSize: 16,
        lineHeight: 19,
        color: color.black,
        marginBottom: 12,
    },
    orderRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 8,
    },
    orderRowBorderTop: {
        borderTopWidth: 1,
        borderTopColor: '#e3e3e3',
        marginTop: 8,
    },
    orderTitleBorderTop: {
        borderTopWidth: 1,
        borderTopColor: '#e3e3e3',
        paddingTop: 16,
        marginTop: 8,
    },
    orderItemLabel: {
        fontFamily: font.ambitRegular,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
    },
    orderItemQty: {
        fontFamily: font.ambitRegular,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
    },
    orderTotalLabel: {
        fontFamily: font.ambitBold,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
    },
    orderTotalValue: {
        fontFamily: font.ambitBold,
        fontSize: 14,
        lineHeight: 20,
        color: color.black,
    },
    cashbackDescription: {
        fontFamily: font.ambitRegular,
        fontSize: 12,
        lineHeight: 17,
        color: color.doveGray,
        marginBottom: 4,
    },
});
